// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC, useEffect, useMemo } from "react";
import { Box, Row, Select, Text } from "@hightouch/htui";
import {
  ColumnType,
  DefaultOperators,
  FilterableColumn,
  filterSupportedOperators,
  OperatorOptions,
  OperatorsWithoutValue,
  PropertyCondition,
  RelativeTimestampOperators,
  TimestampOperator,
  ValueType,
} from "src/types/visual";

import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { isDenied } from "src/components/explore/restrictions";
import { FilterProps } from "./condition";
import { Filter } from "./filter";
import { PropertyInput, PropertyInputProps } from "./property-input";
import { PropertySelector } from "./property-selector";
import {
  formatValue,
  getOperatorLabel,
  updateConditionOperator,
  useConditionComparison,
} from "./utils";
import { ParameterizeCheckbox } from "./parameterize-checkbox";
import { isJsonPathColumn } from "@hightouch/lib/query/visual/types";

const AllowableOptions = [
  {
    label: "value",
    value: "value",
  },
  {
    label: "property",
    value: "property",
  },
  {
    label: "trait",
    value: "trait",
  },
];

type PropertyOperatorValueFilterProps = Pick<
  FilterProps<PropertyCondition>,
  "parent" | "condition" | "onUpdate"
> & {
  isDisabled?: boolean;
  loadingSuggestions: boolean;
  operatorError: string | null | undefined;
  suggestions: PropertyInputProps["suggestions"];
  showParameterizeCheckbox?: boolean;
  includeParentInPropertyComparison?: boolean;
  allowPropertyComparison?: boolean;
  valueError: string | null | undefined;
  columns?: FilterableColumn[] | undefined;
  onSearchUpdate?: (search: string) => void;
};

export const PropertyOperatorValueFilter: FC<
  Readonly<PropertyOperatorValueFilterProps>
> = ({
  condition,
  isDisabled = false,
  loadingSuggestions,
  operatorError,
  parent,
  showParameterizeCheckbox,
  allowPropertyComparison = true,
  includeParentInPropertyComparison,
  suggestions,
  valueError,
  onUpdate,
  onSearchUpdate,
  columns,
}) => {
  const isParameterized = Boolean(condition.propertyOptions?.parameterize);
  const connectionType = parent?.connection?.definition.type;
  const { restrictions } = useQueryBuilderContext();
  const denyRelativeDates = isDenied(restrictions, "relative-dates");
  const {
    audienceTraitRhsFilters,
    isTraitSelected,
    supportsColumnComparison,
    propertyType,
  } = useConditionComparison(condition, { allowPropertyComparison });

  // Filter options based on feature flag. Memoized so each condition row
  // doesn't recompute on every parent re-render (PropertyFilter is rendered
  // once per condition and re-renders frequently as the user edits).
  const ValueOrPropertyOptions = useMemo(
    () =>
      audienceTraitRhsFilters
        ? AllowableOptions
        : AllowableOptions.filter((option) => option.value !== "trait"),
    [audienceTraitRhsFilters],
  );

  const operatorOptions = useMemo(() => {
    if (!propertyType) {
      return undefined;
    }
    const options = OperatorOptions[propertyType].filter(
      filterSupportedOperators(
        connectionType,
        isJsonPathColumn(condition.property),
      ),
    );
    return denyRelativeDates
      ? options.filter(
          (option) =>
            !RelativeTimestampOperators.some((op) => op === option.value),
        )
      : options;
  }, [propertyType, connectionType, condition.property, denyRelativeDates]);

  // Timestamp conditions default to the (relative) `within` operator. When
  // relative dates are denied, move fresh conditions to the first allowed
  // operator instead. Only the `within` + empty-value combination a fresh
  // condition starts with is rewritten; any other combination is saved
  // legacy data (e.g. valueless `anniversary` conditions) that must stay
  // visible and hit the validation error instead.
  useEffect(() => {
    if (
      denyRelativeDates &&
      condition.value == null &&
      condition.operator === TimestampOperator.Within
    ) {
      const fallback = operatorOptions?.[0]?.value;
      if (fallback) {
        onUpdate((condition) => {
          const diff = updateConditionOperator(condition, fallback);
          Object.entries(diff).forEach(([key, value]) => {
            condition[key] = value;
          });
        });
      }
    }
  }, [
    denyRelativeDates,
    condition.operator,
    condition.value,
    operatorOptions,
    onUpdate,
  ]);

  const operatorLabel = getOperatorLabel(condition.operator, propertyType, {
    options: operatorOptions,
    parameterized: isParameterized,
  });
  const formattedValue = formatValue(condition, condition.value);

  const isEmpty =
    condition.value == undefined &&
    condition.value?.type !== ValueType.Column &&
    !OperatorsWithoutValue.includes(condition.operator);

  const isComparingColumns = condition.value?.type === ValueType.Column;

  return (
    <Filter
      fullWidth
      isDisabled={isDisabled}
      isEmpty={isEmpty}
      content={
        <Row alignItems="flex-start" gap={2}>
          {showParameterizeCheckbox && (
            <ParameterizeCheckbox
              isDisabled={isDisabled}
              isChecked={isParameterized}
              onToggle={(checked) => {
                onUpdate((condition) => {
                  condition.operator =
                    DefaultOperators[propertyType ?? ColumnType.Unknown];
                  condition.value = null;
                  if (!condition.propertyOptions) {
                    condition.propertyOptions = {};
                  }
                  condition.propertyOptions.parameterize = checked;
                });
              }}
            />
          )}
          {!isParameterized && (
            <>
              <Select
                removePortal
                isDisabled={isDisabled}
                options={operatorOptions ?? []}
                placeholder="Filter on"
                value={condition.operator}
                width="auto"
                onChange={(value) =>
                  onUpdate((condition) => {
                    const diff = updateConditionOperator(condition, value);
                    Object.entries(diff).forEach(([key, value]) => {
                      condition[key] = value;
                    });
                  })
                }
              />
              {supportsColumnComparison ? (
                <Row gap={2} alignItems="flex-start">
                  <Select
                    isDisabled={isDisabled}
                    options={ValueOrPropertyOptions}
                    width="auto"
                    removePortal
                    value={
                      isTraitSelected
                        ? "trait"
                        : isComparingColumns
                          ? "property"
                          : "value"
                    }
                    onChange={(newComparisonType) => {
                      onUpdate((condition) => {
                        if (newComparisonType === "property") {
                          condition.value = { type: ValueType.Column };
                        } else if (newComparisonType === "trait") {
                          condition.value = {
                            type: ValueType.Column,
                            property: {
                              type: "trait",
                              traitDefinitionId: "",
                              conditions: [],
                            },
                          };
                        } else if (newComparisonType === "value") {
                          condition.value = undefined;
                        }
                      });
                    }}
                  />
                  {isComparingColumns || isTraitSelected ? (
                    <PropertySelector
                      isDisabled={isDisabled}
                      condition={condition}
                      error={valueError}
                      onChange={(valueUpdates) =>
                        onUpdate((condition) => {
                          condition.value = valueUpdates;
                        })
                      }
                      value={condition.value}
                      removePortal
                      parent={parent ?? undefined}
                      includeParentModelProperties={
                        includeParentInPropertyComparison
                      }
                      selectingTraits={isTraitSelected}
                    />
                  ) : (
                    <PropertyInput
                      isDisabled={isDisabled}
                      condition={condition}
                      error={valueError}
                      loading={loadingSuggestions}
                      suggestions={suggestions}
                      onUpdate={onUpdate}
                      onSearchUpdate={onSearchUpdate}
                      columns={columns}
                      // If the newer property comparison is already supported, do not double up on property comparison
                      allowPropertyComparison={false}
                      includeParentInPropertyComparison={
                        includeParentInPropertyComparison
                      }
                      parent={parent ?? undefined}
                    />
                  )}
                </Row>
              ) : (
                <PropertyInput
                  isDisabled={isDisabled}
                  condition={condition}
                  error={valueError}
                  loading={loadingSuggestions}
                  suggestions={suggestions}
                  onUpdate={onUpdate}
                  onSearchUpdate={onSearchUpdate}
                  columns={columns}
                  allowPropertyComparison={allowPropertyComparison}
                  includeParentInPropertyComparison={
                    includeParentInPropertyComparison
                  }
                  parent={parent ?? undefined}
                />
              )}
            </>
          )}
        </Row>
      }
      error={operatorError || valueError}
    >
      <Row gap={1} overflow="hidden">
        {operatorLabel ? (
          <>
            <Text color="text.secondary">{operatorLabel}</Text>{" "}
            <Box
              as={Text}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              minWidth={0}
              flex={1}
              color="text.primary"
              fontWeight="medium"
            >
              {formattedValue}
            </Box>
          </>
        ) : (
          <Text color="text.secondary">Filter on</Text>
        )}
      </Row>
    </Filter>
  );
};
